import React, { useRef, useState } from 'react';
import {
  FileText, Download, Upload, Clock, Trash2, AlertTriangle, Loader2, Users,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BulkOperations = ({ adminKey, onRefresh }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleExport = (type, status, format) => {
    let url;
    if (type === 'ideas') {
      url = `${API_URL}/api/admin-panel/export/ideas?format=${format}${status ? `&status=${status}` : ''}`;
    } else if (type === 'resolved-markets') {
      url = `${API_URL}/api/admin-panel/export/resolved-markets?format=${format}`;
    } else if (type === 'user-statistics') {
      url = `${API_URL}/api/admin-panel/export/user-statistics?format=${format}`;
    }

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', '');

    fetch(url, { headers: { 'X-Admin-Key': adminKey } })
      .then((res) => res.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        link.href = blobUrl;
        link.click();
        window.URL.revokeObjectURL(blobUrl);
      });
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/admin-panel/import/ideas`, {
        method: 'POST',
        headers: { 'X-Admin-Key': adminKey },
        body: formData,
      });

      const result = await response.json();
      setUploadResult(result);
      onRefresh();
    } catch (error) {
      setUploadResult({ errors: [error.message] });
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAllResolved = async () => {
    setDeleting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin-panel/resolved-markets`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': adminKey },
      });
      const result = await response.json();
      alert(result.message);
      setShowDeleteConfirm(false);
      onRefresh();
    } catch (error) {
      alert(`Failed to delete: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6">
      <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <FileText className="h-5 w-5 text-[#3B82F6]" />
        Bulk Operations
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Export Ideas */}
        <div className="bg-[#1e2a45]/50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Download className="h-4 w-4 text-green-400" />
            Export Ideas
          </h3>
          <div className="space-y-2">
            <select
              id="export-status"
              className="w-full bg-[#050810] border border-[#2d3a56] rounded-lg px-3 py-2 text-sm text-white mb-2"
              defaultValue=""
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="live">Live</option>
            </select>
            <div className="flex gap-2">
              <Button onClick={() => handleExport('ideas', document.getElementById('export-status').value, 'csv')} size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs">CSV</Button>
              <Button onClick={() => handleExport('ideas', document.getElementById('export-status').value, 'xlsx')} size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs">Excel</Button>
              <Button onClick={() => handleExport('ideas', document.getElementById('export-status').value, 'txt')} size="sm" className="flex-1 bg-green-600 hover:bg-green-700 text-white text-xs">TXT</Button>
            </div>
          </div>
        </div>

        {/* Import Ideas */}
        <div className="bg-[#1e2a45]/50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Upload className="h-4 w-4 text-blue-400" />
            Import & Update Ideas
          </h3>
          <p className="text-xs text-gray-400 mb-3">
            Upload CSV/Excel/TXT with columns: id, status, admin_notes
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.txt"
            onChange={handleImport}
            className="hidden"
            id="import-file"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
            {uploading ? 'Uploading...' : 'Upload File'}
          </Button>

          {uploadResult && (
            <div className={`mt-3 p-2 rounded-lg text-xs ${uploadResult.errors?.length > 0 ? 'bg-red-500/10 text-red-400' : 'bg-green-500/10 text-green-400'}`}>
              {uploadResult.processed !== undefined && (
                <p>Processed: {uploadResult.processed}, Updated: {uploadResult.updated}, Created: {uploadResult.created}</p>
              )}
              {uploadResult.errors?.length > 0 && (
                <div className="mt-1">
                  <p className="font-semibold">Errors:</p>
                  {uploadResult.errors.slice(0, 3).map((e, i) => <p key={i}>{e}</p>)}
                  {uploadResult.errors.length > 3 && <p>...and {uploadResult.errors.length - 3} more</p>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Resolved Markets */}
        <div className="bg-[#1e2a45]/50 rounded-xl p-4">
          <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4 text-amber-400" />
            Resolved Markets
          </h3>
          <div className="space-y-2">
            <div className="flex gap-2">
              <Button onClick={() => handleExport('resolved-markets', null, 'csv')} size="sm" className="flex-1 bg-amber-600 hover:bg-amber-700 text-white text-xs">CSV</Button>
              <Button onClick={() => handleExport('resolved-markets', null, 'xlsx')} size="sm" className="flex-1 bg-amber-600 hover:bg-amber-700 text-white text-xs">Excel</Button>
            </div>
            <Button
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full bg-red-600 hover:bg-red-700 text-white text-sm"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete All Resolved
            </Button>
          </div>
        </div>
      </div>

      {/* User Statistics - Full Width */}
      <div className="bg-[#1e2a45]/50 rounded-xl p-4 mt-4">
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Users className="h-4 w-4 text-purple-400" />
          User Statistics & Analytics
        </h3>
        <p className="text-xs text-gray-400 mb-3">
          Download comprehensive user data including wallet balances, trade history, positions, and activity metrics
        </p>
        <div className="flex gap-2">
          <Button onClick={() => handleExport('user-statistics', null, 'csv')} size="sm" data-testid="export-user-stats-csv" className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-xs">
            <Download className="h-3 w-3 mr-1" />
            CSV
          </Button>
          <Button onClick={() => handleExport('user-statistics', null, 'xlsx')} size="sm" data-testid="export-user-stats-xlsx" className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-xs">
            <Download className="h-3 w-3 mr-1" />
            Excel
          </Button>
          <Button onClick={() => handleExport('user-statistics', null, 'txt')} size="sm" data-testid="export-user-stats-txt" className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-xs">
            <Download className="h-3 w-3 mr-1" />
            TXT
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md bg-[#050810] border-[#1e2a45]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Confirm Delete
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              This action cannot be undone!
            </DialogDescription>
          </DialogHeader>
          <p className="text-gray-300 text-sm">
            Are you sure you want to delete ALL resolved markets? This will permanently remove them from the database along with all associated trades and positions.
          </p>
          <div className="flex gap-3 mt-4">
            <Button onClick={() => setShowDeleteConfirm(false)} className="flex-1 bg-[#1e2a45] hover:bg-[#2d3a56] text-white">Cancel</Button>
            <Button
              onClick={handleDeleteAllResolved}
              disabled={deleting}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete All'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BulkOperations;
